import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Combobox } from "@/Common/Components/Inputs/Combobox";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { Popup } from "@/Common/Components/Modals/Popup";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { Locale } from "@/Common/Models/Localization/Locale";
import { OntarioPatientEnrollmentModel } from "@/Common/Models/Patient/PatientEnrollment/Ontario/OntarioPatientEnrollmentModel";
import { Person } from "@/Common/Models/Person/Person";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";

enum OntarioPatientEnrollmentPopupType {
    None,
    EditCurrentOrEnroll,
    EditPast,
    Terminate
}

interface IOntarioPatientEnrollmentPopupProps {
    enrollmentPopupType: OntarioPatientEnrollmentPopupType;
    patientEnrollmentModel?: OntarioPatientEnrollmentModel;
    isLoading: boolean;
    onClose: () => void;
    onSaveEnrollment: (patientEnrollmentModel: OntarioPatientEnrollmentModel) => void;
    getPhysician?: (contactFacetId: string) => Person;
}

class OntarioPatientEnrollmentPopupState {
    public patientEnrollmentModel: OntarioPatientEnrollmentModel;
    public selectedClinicians: Person[];
}

class OntarioPatientEnrollmentPopup extends BaseReactComponent<IOntarioPatientEnrollmentPopupProps, OntarioPatientEnrollmentPopupState> {
    private clinicianSearchStrategy: ClinicianSearchStrategy;
    private get isTerminatingEnrollment(): boolean { return this.props.enrollmentPopupType === OntarioPatientEnrollmentPopupType.Terminate; }
    private get isEnrollingOrEditingCurrentEnrollment(): boolean { return this.props.enrollmentPopupType === OntarioPatientEnrollmentPopupType.EditCurrentOrEnroll; }

    public constructor(props: IOntarioPatientEnrollmentPopupProps) {
        super(props);
        const currentPhysician = props.getPhysician?.(props.patientEnrollmentModel?.EnrolledPhysicianId);

        const state = new OntarioPatientEnrollmentPopupState();
        state.patientEnrollmentModel = new OntarioPatientEnrollmentModel(props.patientEnrollmentModel);
        state.selectedClinicians = currentPhysician ? [currentPhysician] : [];
        state.patientEnrollmentModel.EnrollmentStartDate = state.patientEnrollmentModel.EnrollmentStartDate ?? new Date();

        if (props.enrollmentPopupType === OntarioPatientEnrollmentPopupType.Terminate)
            state.patientEnrollmentModel.EnrollmentEndDate = state.patientEnrollmentModel.EnrollmentEndDate ?? new Date();

        this.state = state;
        this.clinicianSearchStrategy = new ClinicianSearchStrategy(cacheStore.GetClinicians(false));
    }

    public componentDidMount(): void {
        this.setAcceptableLocales([Locale.en, Locale.fr], false);
    }

    public render(): JSX.Element {
        const enrollment = this.state.patientEnrollmentModel;

        if (this.props.enrollmentPopupType === OntarioPatientEnrollmentPopupType.None)
            return null;

        const title = this.isTerminatingEnrollment
            ? this.getString("TerminateEnrolmentLabel")
            : this.isEnrollingOrEditingCurrentEnrollment
                ? this.getString("EnrolmentLabel")
                : this.getString("EnrolmentHistoryLabel");

        return (
            <Popup
                title={title}
                manageOverlay
                draggable
                width={600}
                onCloseDelegate={() => this.props.onClose()}
                titleLowerCase
                standardFooterProps={{
                    okButton: {
                        onClick: () => this.props.onSaveEnrollment(this.state.patientEnrollmentModel),
                        disabled: !this.isEnrollmentValid() || this.props.isLoading,
                        label: this.getString("SaveLabel")
                    }
                }}
                acceptableLanguages={[Locale.en, Locale.fr]}
            >
                <div className="padding-md">
                    {!this.isTerminatingEnrollment
                    && (!this.props.patientEnrollmentModel?.EnrolledPhysicianId || !this.isEnrollingOrEditingCurrentEnrollment)
                    && (
                        <div className="row margin-sm-bottom">
                            <div className="col-md-4 vcenter text-right">
                                <label className="form-label">{this.getString("PhysicianNameLabel")}</label>
                            </div>
                            <div className="col-md-8">
                                <PersonSearchAutocompleteCustom
                                    focusOnLoad
                                    valid={!!enrollment?.EnrolledPhysicianId}
                                    componentId="Physician_OntarioPatientEnrollment"
                                    searchStrategy={this.clinicianSearchStrategy}
                                    selectedItemList={(this.state.selectedClinicians ?? []).toImmutable()}
                                    onSelectionCallback={personItem => this.onSelectPhysician(personItem)}
                                    onClearCallback={() => this.onRemovePhysician()}
                                    closeDropDownAfterSelection
                                    dropDownListMaxHeight={100}
                                />
                            </div>
                        </div>
                    )
                    }
                    {!this.isTerminatingEnrollment && (
                        <div className="row margin-sm-bottom">
                            <div className="col-md-4 vcenter text-right">
                                <label className="form-label">{this.getString("EnrolmentStartDateLabel")}</label>
                            </div>
                            <div className="col-md-8">
                                <DateSelector
                                    id="EnrolmentStartDateLabel"
                                    date={enrollment?.EnrollmentStartDate}
                                    onChangeCallback={date => this.mutateState(s => s.patientEnrollmentModel.EnrollmentStartDate = date)}
                                    dateFormat="yyyy-MM-dd"
                                    valid={this.isDateValid()}
                                    cssClass="date-selector"
                                    height={34}
                                />
                            </div>
                        </div>
                    )}
                    {!this.isEnrollingOrEditingCurrentEnrollment && (
                        <div className="row margin-sm-bottom">
                            <div className="col-md-4 vcenter text-right">
                                <label className="form-label">{this.getString("EnrolmentEndDateLabel")}</label>
                            </div>
                            <div className="col-md-8">
                                <DateSelector
                                    id="EnrollmentEndDateLabel"
                                    date={enrollment?.EnrollmentEndDate}
                                    onChangeCallback={date => this.mutateState(s => s.patientEnrollmentModel.EnrollmentEndDate = date)}
                                    dateFormat="yyyy-MM-dd"
                                    valid={this.isDateValid()}
                                    cssClass="date-selector"
                                    height={34}
                                    minDate={enrollment && this.isTerminatingEnrollment ? enrollment.EnrollmentStartDate : undefined}
                                />
                            </div>
                        </div>
                    )}
                    {!this.isEnrollingOrEditingCurrentEnrollment && (
                        <div className="row margin-sm-bottom">
                            <div className="col-md-4 vcenter text-right">
                                <label className="form-label">{this.getString("TerminationReasonLabel")}</label>
                            </div>
                            <div className="col-md-8">
                                <Combobox
                                    showTooltips
                                    id="EnrollmentTerminateReasonCode"
                                    className="no-padding"
                                    onChange={code => this.mutateState(s => s.patientEnrollmentModel.TerminationReasonCode = Number(code))}
                                    selectedCode={enrollment?.TerminationReasonCode?.toString() ?? ""}
                                    HasEmptyItem
                                    items={OntarioPatientEnrollmentModel.GetTerminationCodeComboboxItems()?.toImmutable()}
                                    valid={!!enrollment?.TerminationReasonCode}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </Popup>
        );
    }

    private onSelectPhysician(physician: PersonDropdownItem): void {
        this.mutateState(s => {
            s.selectedClinicians = [physician.Item];
            s.patientEnrollmentModel.EnrolledPhysicianId = physician.Item.ContactFacetId;
        });
    }

    private onRemovePhysician(): void {
        this.mutateState(s => {
            s.selectedClinicians = [];
            s.patientEnrollmentModel.EnrolledPhysicianId = null;
        });
    }

    private isEnrollmentValid(): boolean {
        const enrollment = this.state.patientEnrollmentModel;

        if (!this.isTerminatingEnrollment && !enrollment?.EnrolledPhysicianId)
            return false;

        if (!this.isEnrollingOrEditingCurrentEnrollment && !enrollment?.TerminationReasonCode)
            return false;

        return this.isDateValid();
    }

    private isDateValid(): boolean {
        const enrollment = this.state.patientEnrollmentModel;
        const parsedStartDate = DateOnly.parseUnlessNull(enrollment?.EnrollmentStartDate)?.toLuxon();
        const parsedEndDate = DateOnly.parseUnlessNull(enrollment?.EnrollmentEndDate)?.toLuxon();

        if (!this.isTerminatingEnrollment && !parsedStartDate?.isValid)
            return false;

        if (!this.isEnrollingOrEditingCurrentEnrollment && (!parsedEndDate?.isValid || parsedStartDate > parsedEndDate))
            return false;

        return true;
    }
}

export { OntarioPatientEnrollmentPopup, OntarioPatientEnrollmentPopupType };
